import {
  callMembersOf,
  LiveHuddleContext,
} from "@app/components/live/liveHuddleContext";
import { MemoairLiveHostProvider } from "@app/components/live/MemoairLiveHost";
import { useConversationParticipants } from "@app/hooks/conversations/useConversationParticipants";
import {
  useCreateConversationLiveDoc,
  useLiveDocs,
} from "@app/lib/swr/live_docs";
import type { LightWorkspaceType } from "@app/types/user";
import { Button, XClose } from "@dust-tt/sparkle";
import { LiveObjectView } from "@memo-air/react/all";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSWRConfig } from "swr";

type Huddle = { conversationId: string; callId: string; spaceId: string };

// A conversation's huddle, as AirLink draws it: the call opens over the page, with the
// conversation's space as its stage, so the people and agents in it work in the same room.
// Closing it leaves the call; the call and space stay the conversation's, made on first use.
export function LiveHuddleProvider({
  owner,
  children,
}: {
  owner: LightWorkspaceType;
  children: ReactNode;
}) {
  const [huddle, setHuddle] = useState<Huddle | null>(null);
  const [isInCall, setIsInCall] = useState(false);
  const { liveDocs } = useLiveDocs({ owner });
  const createLiveDoc = useCreateConversationLiveDoc({ owner });
  // The chat's huddle cards follow a huddle opening and closing without waiting for a refresh.
  const { mutate } = useSWRConfig();
  const refreshHuddleCards = useCallback(
    () =>
      void mutate(
        (key) => typeof key === "string" && key.includes("/live-docs/huddles")
      ),
    [mutate]
  );

  const openHuddle = useCallback(
    async (conversationId: string, title: string | null) => {
      const existing = (kind: "media" | "space") =>
        liveDocs.find(
          (doc) => doc.conversationId === conversationId && doc.kind === kind
        )?.sId;
      const callId =
        existing("media") ??
        (
          await createLiveDoc({
            conversationId,
            kind: "media",
            title: "Huddle",
          })
        )?.sId;
      const spaceId =
        existing("space") ??
        (
          await createLiveDoc({
            conversationId,
            kind: "space",
            title: title ?? "Space",
          })
        )?.sId;
      if (callId && spaceId) {
        setHuddle({ conversationId, callId, spaceId });
        refreshHuddleCards();
      }
    },
    [liveDocs, createLiveDoc, refreshHuddleCards]
  );

  const value = useMemo(
    () => ({
      openHuddle,
      inHuddleConversationId: huddle && isInCall ? huddle.conversationId : null,
    }),
    [openHuddle, huddle, isInCall]
  );

  return (
    <LiveHuddleContext.Provider value={value}>
      {children}
      {huddle && (
        <HuddleModal
          key={huddle.callId}
          owner={owner}
          huddle={huddle}
          onInCall={setIsInCall}
          onClose={() => {
            setIsInCall(false);
            setHuddle(null);
            refreshHuddleCards();
          }}
        />
      )}
    </LiveHuddleContext.Provider>
  );
}

const huddleExitMs = 180;

function HuddleModal({
  owner,
  huddle,
  onInCall,
  onClose,
}: {
  owner: LightWorkspaceType;
  huddle: Huddle;
  onInCall: (inCall: boolean) => void;
  onClose: () => void;
}) {
  const { conversationParticipants } = useConversationParticipants({
    conversationId: huddle.conversationId,
    workspaceId: owner.sId,
  });
  const agents = conversationParticipants?.agents ?? [];
  const closeRef = useRef<HTMLButtonElement>(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    closeRef.current?.focus();
    return () => clearTimeout(leaveTimerRef.current ?? undefined);
  }, []);
  // The exit plays before the call view unmounts; a timer, so it never waits on animationend.
  const close = () => {
    if (leaveTimerRef.current) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      onClose();
      return;
    }
    setIsLeaving(true);
    leaveTimerRef.current = setTimeout(onClose, huddleExitMs);
  };

  return (
    <div
      className={
        isLeaving
          ? "fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[2px] animate-out fade-out-0 fill-mode-forwards duration-200 motion-reduce:animate-none"
          : "fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[2px] animate-in fade-in-0 duration-300 motion-reduce:animate-none"
      }
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Huddle"
        className={
          isLeaving
            ? "relative flex h-[calc(100dvh-1.5rem)] w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-2xl md:h-[75vh] md:w-[75vw] border border-border bg-background shadow-2xl animate-out fade-out-0 zoom-out-[0.98] fill-mode-forwards duration-200 ease-in dark:border-border-night dark:bg-background-night motion-reduce:animate-none"
            : "relative flex h-[calc(100dvh-1.5rem)] w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-2xl md:h-[75vh] md:w-[75vw] border border-border bg-background shadow-2xl animate-in fade-in-0 zoom-in-[0.96] slide-in-from-bottom-4 duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] dark:border-border-night dark:bg-background-night motion-reduce:animate-none"
        }
      >
        <MemoairLiveHostProvider owner={owner}>
          <LiveObjectView
            className="min-h-0 flex-1"
            docId={huddle.callId}
            agents={agents}
            callMembers={callMembersOf(conversationParticipants)}
            onInCall={onInCall}
            stage={
              <LiveObjectView
                className="isolate min-h-0 flex-1"
                docId={huddle.spaceId}
                agents={agents}
                renderHeader={({ title, actions }) => (
                  <div className="flex h-10 shrink-0 items-center gap-2 border-b border-border px-3 dark:border-border-night">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground dark:text-foreground-night">
                      {title}
                    </span>
                    {actions}
                  </div>
                )}
              />
            }
            renderHeader={({ title, actions }) => (
              <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3 dark:border-border-night">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground dark:text-foreground-night">
                  {title}
                </span>
                {actions}
                <Button
                  ref={closeRef}
                  size="xs"
                  variant="ghost"
                  icon={XClose}
                  tooltip="Back to the chat"
                  aria-label="Back to the chat"
                  onClick={close}
                />
              </div>
            )}
          />
        </MemoairLiveHostProvider>
      </div>
    </div>
  );
}
